import { supabase } from './supabase';

export type LeadProductBron = 'Office-App' | 'Phone-Agent' | 'Brein' | 'Client-App' | 'Kantoor-Handmatig';
export type KleedVorm = 'rechthoekig' | 'rond' | 'overig';

export interface LeadProductMutationResult {
  success: boolean;
  message?: string;
  errorCode?: string;
  leadProductId?: string;
  leadId?: string;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
  return h;
}

async function postLeadProduct(body: Record<string, unknown>): Promise<LeadProductMutationResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/offertes/lead-product', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({} as LeadProductMutationResult));
  if (!response.ok && data.success !== true) {
    return {
      success: false,
      message: data.message || `Mutatie mislukt (${response.status})`,
      errorCode: data.errorCode,
    };
  }
  return data as LeadProductMutationResult;
}

export interface AddLeadProductArgs {
  leadId: string;
  productCode?: string;
  vorm?: KleedVorm;       // verplicht voor vorm-based primaries; optioneel voor Kussen
  lengteCm?: number | null;
  breedteCm?: number | null;
  diameterCm?: number | null;
  oppervlakteM2?: number | null;
  aantal?: number | null; // verplicht voor count-based (Kussen)
  bijzonderheden?: string | null;
  externeReferentie?: string | null;
  bron?: LeadProductBron;
}

export async function addLeadProduct(args: AddLeadProductArgs): Promise<LeadProductMutationResult> {
  return postLeadProduct({
    action: 'add',
    leadId: args.leadId,
    productCode: args.productCode ?? null,
    vorm: args.vorm ?? null,
    lengteCm: args.lengteCm ?? null,
    breedteCm: args.breedteCm ?? null,
    diameterCm: args.diameterCm ?? null,
    oppervlakteM2: args.oppervlakteM2 ?? null,
    aantal: args.aantal ?? null,
    bijzonderheden: args.bijzonderheden ?? null,
    externeReferentie: args.externeReferentie ?? null,
    bron: args.bron || 'Office-App',
  });
}

export interface UpdateLeadProductArgs {
  leadProductId: string;
  lengteCm?: number | null;
  breedteCm?: number | null;
  diameterCm?: number | null;
  oppervlakteM2?: number | null;
  bijzonderheden?: string | null;
  externeReferentie?: string | null;
  bron?: LeadProductBron;
}

export async function updateLeadProduct(args: UpdateLeadProductArgs): Promise<LeadProductMutationResult> {
  const payload: Record<string, unknown> = {
    action: 'update',
    leadProductId: args.leadProductId,
    bron: args.bron || 'Office-App',
  };
  if (args.lengteCm !== undefined) payload.lengteCm = args.lengteCm;
  if (args.breedteCm !== undefined) payload.breedteCm = args.breedteCm;
  if (args.diameterCm !== undefined) payload.diameterCm = args.diameterCm;
  if (args.oppervlakteM2 !== undefined) payload.oppervlakteM2 = args.oppervlakteM2;
  if (args.bijzonderheden !== undefined) payload.bijzonderheden = args.bijzonderheden;
  if (args.externeReferentie !== undefined) payload.externeReferentie = args.externeReferentie;
  return postLeadProduct(payload);
}

export interface RemoveLeadProductArgs {
  leadProductId: string;
  bron?: LeadProductBron;
}

export async function removeLeadProduct(args: RemoveLeadProductArgs): Promise<LeadProductMutationResult> {
  return postLeadProduct({
    action: 'remove',
    leadProductId: args.leadProductId,
    bron: args.bron || 'Office-App',
  });
}
